/**
 * Filas clave/valor para los campos JSON libres de la API.
 *
 * Dos endpoints del módulo 2 aceptan un objeto JSON sin esquema fijo:
 *  - `datos` de un método de pago (número de cuenta, titular…);
 *  - las credenciales de un canal con JSON libre (el backend lo acepta; hoy ningún canal del panel lo
 *    usa: TikTok salió del frontend el 20-sep-2026 y volverá con su API), así que
 *    cualquier JSON no vacío).
 *
 * El usuario los edita como pares clave/valor (`KeyValueFields`) y esta capa hace la
 * conversión de ida y vuelta, ignorando las filas vacías.
 */

export interface KeyValueRow {
  /** Identificador solo para la UI (`key` de React); no viaja al backend. */
  id: string;
  key: string;
  value: string;
}

let rowCounter = 0;

/** Fila vacía nueva (id único por fila para poder borrarlas y reordenarlas). */
export function emptyKeyValueRow(key = '', value = ''): KeyValueRow {
  rowCounter += 1;
  return { id: `kv-${rowCounter}`, key, value };
}

/** Objeto del backend → filas del formulario (vacío = al menos una fila en blanco). */
export function keyValueRowsFromRecord(record: Record<string, unknown> | null | undefined): KeyValueRow[] {
  if (!record) return [emptyKeyValueRow()];

  const rows = Object.entries(record).map(([key, value]) =>
    emptyKeyValueRow(key, typeof value === 'string' ? value : JSON.stringify(value)),
  );

  return rows.length > 0 ? rows : [emptyKeyValueRow()];
}

/**
 * Filas del formulario → objeto del backend.
 * Se descartan las filas sin clave; `null` cuando no queda ninguna (para el `PUT` de
 * credenciales, el backend exige al menos una y responde 400).
 */
export function recordFromKeyValueRows(rows: KeyValueRow[]): Record<string, string> | null {
  const entries = rows
    .map((row) => [row.key.trim(), row.value.trim()] as const)
    .filter(([key]) => key.length > 0);

  if (entries.length === 0) return null;

  return Object.fromEntries(entries);
}

/** ¿Tiene alguna fila con clave? (para saber si el usuario escribió algo). */
export function hasKeyValueContent(rows: KeyValueRow[]): boolean {
  return rows.some((row) => row.key.trim().length > 0);
}
